import { useCallback, useEffect, useMemo, useState } from 'react';
import { DeepMap, FieldError, useFormContext } from 'react-hook-form';
import { isEqual } from 'lodash';
import { useQuery } from '@apollo/client';
// Types
import {
  ConnectedSocialAccounts,
  ConnectedSocialAccounts_connectedSocialAccounts,
} from 'api/auth/types/ConnectedSocialAccounts';
// Constants
import { LINKABLE_SOCIALS_OPTIONS, SOCIALS_OPTIONS } from 'constants/socials';
// Helpers
import {
  deriveUsedPlatforms,
  firstAvailablePlatform,
} from 'components/Onboarding/SocialMediaStep/SocialMediaFields/helpers';
// Context
import { OnboardingFormData, SocialAccount } from 'context/OnboardingContext';
import {
  SocialMediaConnectionProvider,
  SocialMediaType,
} from 'api/graphql-global-types';
import { CONNECTED_SOCIAL_ACCOUNTS } from 'api/auth/queries';
// Ui
import Button from 'ui3/Button/Button';
import SocialsRow from 'ui3/SocialsRow/SocialsRow';
import AddSocialAccountModal from 'components/common/AddSocialAccountModal/AddSocialAccountModal';
import SocialsRowWithIntegration from 'ui3/SocialsRowWithIntegration/SocialsRowWithIntegration';
// Hooks
import { useOnboardingContext } from 'hooks/index';
import { useSocialIntegrationListener } from 'hooks/useSocialIntegrationListener';
// Styles
import styles from './SocialMediaFields.module.scss';

type SocialMediaFieldsProps = {
  socialAccounts?: SocialAccount[];
  errors: DeepMap<SocialAccount[] | undefined, FieldError> | FieldError;
  onChange: (socialAccounts: SocialAccount[]) => void;
};

const SocialMediaFields = ({
  socialAccounts,
  errors,
  onChange,
}: SocialMediaFieldsProps) => {
  const {
    data: connectedSocialAccountsData,
  } = useQuery<ConnectedSocialAccounts>(CONNECTED_SOCIAL_ACCOUNTS);

  const { getValues } = useFormContext<OnboardingFormData>();
  const {
    onboardingStep,
    otpValidation,
    isAddSocialAccountModalOpen,
    setIsAddSocialAccountModalOpen,
  } = useOnboardingContext();

  const [linkableSocialAccounts, setLinkableSocialAccounts] = useState<
    Partial<SocialAccount>[]
  >(
    LINKABLE_SOCIALS_OPTIONS.map((option) => ({
      followersCount: undefined,
      platform: option.value,
      link: '',
      isAdded: false,
    }))
  );

  const [localSocialAccounts, setLocalSocialAccounts] = useState<
    Partial<SocialAccount>[]
  >(
    socialAccounts?.length
      ? socialAccounts
      : [
          {
            platform: undefined,
            link: '',
            isAdded: false,
          },
        ]
  );

  const connectedByPlatform = useMemo(() => {
    const map = new Map<
      SocialMediaType,
      ConnectedSocialAccounts_connectedSocialAccounts
    >();
    connectedSocialAccountsData?.connectedSocialAccounts?.forEach((account) => {
      map.set(account.platform, account);
    });
    return map;
  }, [connectedSocialAccountsData]);

  useEffect(() => {
    setLinkableSocialAccounts(
      LINKABLE_SOCIALS_OPTIONS.map((option) => {
        const connected = connectedByPlatform.get(option.value);
        return {
          platform: option.value,
          link: connected?.username || '',
          isAdded: Boolean(connected),
          followersCount: connected?.followersCount ?? undefined,
        };
      })
    );
  }, [connectedByPlatform]);

  useEffect(() => {
    if (!isEqual(localSocialAccounts, socialAccounts)) {
      onChange(localSocialAccounts as SocialAccount[]);
    }
  }, [localSocialAccounts, socialAccounts, onChange]);

  const usedPlatforms = useMemo(
    () => deriveUsedPlatforms(localSocialAccounts, connectedByPlatform.keys()),
    [localSocialAccounts, connectedByPlatform]
  );

  // Instagram/Facebook/TikTok are only obtainable through the OAuth integration
  // rows above, so the manual flow is limited to SOCIALS_OPTIONS.
  const hasAvailablePlatform = SOCIALS_OPTIONS.some(
    (option) => !usedPlatforms.includes(option.value)
  );

  const handleOpenAddAccountModal = useCallback(() => {
    setIsAddSocialAccountModalOpen(true);

    const lastAddedSocialAccount =
      localSocialAccounts?.[localSocialAccounts.length - 1];
    const nextPlatform = firstAvailablePlatform(SOCIALS_OPTIONS, usedPlatforms);

    if (!lastAddedSocialAccount?.link && !lastAddedSocialAccount?.platform) {
      if (lastAddedSocialAccount && nextPlatform) {
        setLocalSocialAccounts((prevState) =>
          prevState.map((account, index) =>
            index === prevState.length - 1
              ? { ...account, platform: nextPlatform }
              : account
          )
        );
      }
      return;
    }

    setLocalSocialAccounts((prevState) => [
      ...prevState,
      {
        platform: nextPlatform,
        link: '',
        isAdded: false,
      },
    ]);
  }, [localSocialAccounts, usedPlatforms, setIsAddSocialAccountModalOpen]);

  const handleAddSocialAccount = () => {
    const lastAddedSocialAccount =
      localSocialAccounts?.[localSocialAccounts.length - 1];

    if (
      lastAddedSocialAccount?.link &&
      lastAddedSocialAccount?.platform &&
      !lastAddedSocialAccount?.isAdded
    ) {
      setLocalSocialAccounts((prevState) =>
        prevState.map((account, index) => {
          if (index === prevState.length - 1) {
            return {
              ...account,
              isAdded: true,
            };
          }
          return account;
        })
      );
    }
  };

  const onSocialAccountChange = useCallback(
    (newValue: Partial<SocialAccount>) => {
      setLocalSocialAccounts((prevLocalSocialAccounts) =>
        prevLocalSocialAccounts.map((account, i) => {
          if (i === prevLocalSocialAccounts.length - 1) {
            return {
              ...account,
              ...newValue,
            };
          }
          return account;
        })
      );
    },
    []
  );

  const handleRemoveSocialAccount = (socialAccount: SocialAccount) => {
    if (localSocialAccounts.length === 1) {
      setLocalSocialAccounts([
        {
          platform: undefined,
          link: '',
          isAdded: false,
        },
      ]);
    } else {
      setLocalSocialAccounts((prevState) =>
        prevState.filter(
          (account) => account.platform !== socialAccount.platform
        )
      );
    }
  };

  const reversedIndex = localSocialAccounts
    .slice()
    .reverse()
    .findIndex((account) => account.isAdded);
  const lastAddedRowIndex =
    reversedIndex === -1 ? -1 : localSocialAccounts.length - 1 - reversedIndex;

  const {
    savePendingIntegration,
    pendingProvider,
  } = useSocialIntegrationListener();

  const onLinkAccountPress = async (
    platform?: SocialMediaConnectionProvider
  ) => {
    if (!platform) return;
    const { avatar, ...values } = getValues();
    await savePendingIntegration(
      {
        values: {
          ...values,
          ...(typeof avatar === 'string' ? { avatar } : {}),
        },
        step: onboardingStep,
        otpValidation,
      },
      platform,
      avatar instanceof File ? { avatar } : undefined
    );
  };

  return (
    <div className={styles.root}>
      {linkableSocialAccounts.map((socialAccount) => (
        <SocialsRowWithIntegration
          loading={
            pendingProvider ===
            ((socialAccount?.platform as unknown) as SocialMediaConnectionProvider)
          }
          key={socialAccount.platform}
          socialAccount={socialAccount as SocialAccount}
          isLinked={Boolean(
            socialAccount.platform &&
              connectedByPlatform.get(socialAccount.platform as SocialMediaType)
          )}
          onLinkAccountPress={() =>
            onLinkAccountPress(
              (socialAccount?.platform as unknown) as SocialMediaConnectionProvider
            )
          }
        />
      ))}
      {localSocialAccounts?.map((socialAccount, index) => {
        if (!socialAccount.isAdded) {
          return null;
        }

        return (
          <SocialsRow
            key={socialAccount.platform}
            socialAccount={socialAccount as SocialAccount}
            firstRow={index === 0}
            lastRow={index === lastAddedRowIndex}
            onRemove={() =>
              handleRemoveSocialAccount(socialAccount as SocialAccount)
            }
          />
        );
      })}

      {hasAvailablePlatform && (
        <Button
          variant="primary"
          subVariant="text"
          icon="plus"
          onClick={handleOpenAddAccountModal}
        >
          Add Social Account
        </Button>
      )}

      <AddSocialAccountModal
        isOpen={isAddSocialAccountModalOpen}
        onClose={() => setIsAddSocialAccountModalOpen(false)}
        onCancel={() => {
          setLocalSocialAccounts((prevState) =>
            prevState.map((account, index) => {
              if (index === prevState.length - 1) {
                return {
                  platform: undefined,
                  link: '',
                  isAdded: false,
                };
              }
              return account;
            })
          );
        }}
        errors={{
          platform:
            errors?.[(localSocialAccounts ?? []).length - 1]?.platform?.message,
          link: errors?.[(localSocialAccounts ?? []).length - 1]?.link?.message,
        }}
        value={localSocialAccounts?.[(localSocialAccounts ?? []).length - 1]}
        onChange={onSocialAccountChange}
        onConfirm={handleAddSocialAccount}
        platformOptions={SOCIALS_OPTIONS}
        usedPlatforms={usedPlatforms}
      />
    </div>
  );
};

export default SocialMediaFields;
